import {
  ChangeDetectionStrategy, Component, EventEmitter, Output, inject,
  signal, computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MarkersService, type MarkerSource } from '../services/markers.service.js';
import { TooltipDirective } from '../directives/tooltip.directive.js';

interface IssuedCode {
  code: string;
  sourceId: string;
  expiresAt: number; // ms
}

@Component({
  selector: 'app-marker-sources-modal',
  standalone: true,
  imports: [CommonModule, TooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'onEscape()',
  },
  template: `
    <div class="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
         (click)="close.emit()">
      <div class="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col"
           (click)="$event.stopPropagation()">

        <!-- Header -->
        <header class="flex items-center justify-between p-4 border-b border-gray-800 shrink-0">
          <div class="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <h2 class="text-lg font-bold text-gray-100">Подключения StreamMarkers</h2>
          </div>
          <button (click)="close.emit()" appTooltip="Закрыть (Esc)"
                  class="p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <!-- Generate code section -->
        <div class="p-4 border-b border-gray-800 shrink-0 space-y-3">
          @if (issuedCode(); as code) {
            <div class="bg-cyan-950/40 border border-cyan-700/50 rounded-lg p-4 text-center space-y-2">
              <p class="text-[11px] text-cyan-300 uppercase tracking-wider">Код привязки</p>
              <p class="text-3xl font-mono font-bold text-cyan-200 tracking-[0.2em] select-all">{{ code.code }}</p>
              <p class="text-xs text-gray-400">
                Истекает через {{ remainingMinutes() }} мин · Введите код в StreamMarkers
              </p>
              <div class="flex gap-2 justify-center pt-1">
                <button (click)="copyCode(code.code)"
                        class="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded text-xs text-gray-200 transition-colors">
                  {{ wasCopied() ? 'Скопировано ✓' : 'Скопировать' }}
                </button>
                <button (click)="dismissCode()"
                        class="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded text-xs text-gray-400 hover:text-gray-200 transition-colors">
                  Закрыть код
                </button>
              </div>
            </div>
          } @else {
            <p class="text-xs text-gray-400">
              Создайте код, введите имя копии StreamMarkers (например, «Моя» или «Монтажёр Вася»). Код одноразовый, действует 15 минут.
            </p>
            <div class="flex gap-2">
              <input type="text"
                     [value]="newSourceName()"
                     (input)="newSourceName.set($any($event.target).value)"
                     (keydown.enter)="generateCode()"
                     [disabled]="isGenerating()"
                     placeholder="Имя копии"
                     maxlength="50"
                     class="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-100 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none disabled:opacity-50" />
              <button (click)="generateCode()"
                      [disabled]="!newSourceName().trim() || isGenerating()"
                      class="px-4 py-2 bg-cyan-700/60 hover:bg-cyan-600/70 text-cyan-100 border border-cyan-700/50 rounded text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0">
                @if (isGenerating()) { <span>...</span> } @else { <span>Создать код</span> }
              </button>
            </div>
            @if (errorMessage()) {
              <p class="text-xs text-red-400">{{ errorMessage() }}</p>
            }
          }
        </div>

        <!-- Sources list -->
        <div class="flex-1 overflow-y-auto p-4">
          <p class="text-[11px] text-gray-500 uppercase tracking-wider mb-3">Привязанные копии · {{ sources().length }}</p>

          @if (sources().length === 0) {
            <p class="text-center text-sm text-gray-500 italic py-4">Нет подключённых источников</p>
          } @else {
            <div class="space-y-2">
              @for (source of sources(); track source.sourceId) {
                <div class="bg-gray-800/50 border border-gray-700/50 rounded-lg p-3">
                  <div class="flex items-center justify-between gap-2">
                    @if (renamingId() === source.sourceId) {
                      <input type="text"
                             [value]="renameValue()"
                             (input)="renameValue.set($any($event.target).value)"
                             (keydown.enter)="confirmRename(source)"
                             (keydown.escape)="cancelRename()"
                             autofocus
                             maxlength="50"
                             class="flex-1 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100 focus:border-cyan-500 outline-none" />
                      <button (click)="confirmRename(source)" appTooltip="Сохранить"
                              class="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-green-400">
                        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                      </button>
                      <button (click)="cancelRename()" appTooltip="Отмена"
                              class="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200">
                        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    } @else {
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-semibold text-gray-100 truncate">{{ source.name }}</p>
                        <p class="text-[11px] text-gray-500 mt-0.5">
                          @if (!source.pairedAt) {
                            <span class="text-amber-400">⏳ Ожидает привязки</span>
                          } @else if (source.lastActiveAt) {
                            <span>Активен · {{ formatRelative(source.lastActiveAt) }}</span>
                          } @else {
                            <span>Привязан · {{ formatRelative(source.pairedAt) }}</span>
                          }
                        </p>
                      </div>
                      <button (click)="startRename(source)" appTooltip="Переименовать"
                              class="p-1.5 rounded text-gray-500 hover:text-gray-200 hover:bg-gray-700/50 transition-colors">
                        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button (click)="revoke(source)" appTooltip="Отозвать (запретить запись)"
                              class="p-1.5 rounded text-gray-500 hover:text-red-400 hover:bg-red-900/30 transition-colors">
                        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class MarkerSourcesModalComponent {
  @Output() close = new EventEmitter<void>();

  private markersService = inject(MarkersService);

  sources = this.markersService.markerSources;

  newSourceName = signal<string>('');
  isGenerating = signal<boolean>(false);
  errorMessage = signal<string>('');

  issuedCode = signal<IssuedCode | null>(null);
  wasCopied = signal<boolean>(false);
  private codeTimerId: any = null;

  // For inline rename UI
  renamingId = signal<string | null>(null);
  renameValue = signal<string>('');

  // Reactive countdown — refreshed via window.setInterval-driven signal
  private now = signal<number>(Date.now());
  remainingMinutes = computed(() => {
    const code = this.issuedCode();
    if (!code) return 0;
    return Math.max(0, Math.ceil((code.expiresAt - this.now()) / 60000));
  });

  constructor() {
    // Tick once per 10 s — enough for "minutes left" granularity.
    const tickId = window.setInterval(() => this.now.set(Date.now()), 10000);
    this.codeTimerId = tickId;
  }

  ngOnDestroy(): void {
    if (this.codeTimerId) window.clearInterval(this.codeTimerId);
  }

  async generateCode(): Promise<void> {
    const name = this.newSourceName().trim();
    if (!name || this.isGenerating()) return;
    this.errorMessage.set('');
    this.isGenerating.set(true);
    try {
      const result = await this.markersService.createPairingCode(name);
      this.issuedCode.set(result);
      this.newSourceName.set('');
      this.wasCopied.set(false);

      // Auto-clear when expired so the UI flips back to the input.
      const ttlMs = result.expiresAt - Date.now();
      if (ttlMs > 0) {
        window.setTimeout(() => {
          if (this.issuedCode()?.code === result.code) this.issuedCode.set(null);
        }, ttlMs + 500);
      }
    } catch (err: any) {
      console.error('createPairingCode failed:', err);
      this.errorMessage.set(err?.message || 'Не удалось создать код. Попробуйте ещё раз.');
    } finally {
      this.isGenerating.set(false);
    }
  }

  copyCode(code: string): void {
    navigator.clipboard?.writeText(code).then(() => {
      this.wasCopied.set(true);
      window.setTimeout(() => this.wasCopied.set(false), 1500);
    }).catch((err) => console.warn('Clipboard write failed:', err));
  }

  dismissCode(): void {
    this.issuedCode.set(null);
    this.wasCopied.set(false);
  }

  startRename(source: MarkerSource): void {
    this.renamingId.set(source.sourceId);
    this.renameValue.set(source.name);
  }

  cancelRename(): void {
    this.renamingId.set(null);
    this.renameValue.set('');
  }

  async confirmRename(source: MarkerSource): Promise<void> {
    const newName = this.renameValue().trim();
    if (!newName || newName === source.name) {
      this.cancelRename();
      return;
    }
    try {
      await this.markersService.renameSource(source.sourceId, newName);
    } catch (err) {
      console.error('renameSource failed:', err);
    } finally {
      this.cancelRename();
    }
  }

  async revoke(source: MarkerSource): Promise<void> {
    if (!confirm(`Отозвать «${source.name}»? Эта копия StreamMarkers больше не сможет писать новые метки. Существующие метки сохранятся.`)) return;
    try {
      await this.markersService.revokeSource(source.sourceId);
    } catch (err) {
      console.error('revokeSource failed:', err);
    }
  }

  onEscape(): void {
    if (this.renamingId()) {
      this.cancelRename();
      return;
    }
    this.close.emit();
  }

  formatRelative(ts: any): string {
    if (!ts) return '—';
    // Firestore Timestamp or ISO string or Date
    let date: Date;
    if (ts.toDate) date = ts.toDate();
    else if (typeof ts === 'string') date = new Date(ts);
    else if (typeof ts === 'number') date = new Date(ts);
    else return '—';

    const diffMs = Date.now() - date.getTime();
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'только что';
    if (minutes < 60) return `${minutes} мин назад`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} ч назад`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} дн назад`;
    return date.toLocaleDateString('ru-RU');
  }
}
